/**
 * Api endpoints for Owner Operators
 */

import { USER_STATUS } from '../../../providers/User/User.model';
import { BaseQueryParams } from '../../Global.model';
import { twApi } from '../api';
import { SettlementDetailDriverPay } from '../settlement/settlement.model';
import {
  CreateOwnerOperatorParams,
  OwnerOperator,
  OwnerOperatorTripPayDefault,
  UpdateOwnerOperatorParams
} from './ownerOperators.model';

const ownerOperatorsApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    createOwnerOperator: builder.mutation<OwnerOperator, CreateOwnerOperatorParams>({
      query: (data) => ({
        url: '/owner-operators',
        method: 'POST',
        body: data
      }),
      transformResponse: (response: { data: OwnerOperator }) => response.data,
      invalidatesTags: ['OwnerOperators']
    }),
    getOwnerOperators: builder.query<
      { content: OwnerOperator[]; total: number },
      { status?: USER_STATUS } & BaseQueryParams
    >({
      query: (query) => ({
        url: '/owner-operators',
        method: 'GET',
        params: { ...query }
      }),
      transformResponse: (response: { data: OwnerOperator[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result = { content: [], total: 0 }) =>
        result?.content
          ? [
              ...result.content.map(({ id }: { id: string }) => ({
                type: 'OwnerOperators' as const,
                id
              })),
              { type: 'OwnerOperators', id: 'OWNER_OPERATORS_LIST' }
            ]
          : [{ type: 'OwnerOperators', id: 'OWNER_OPERATORS_LIST' }]
    }),
    getOwnerOperatorById: builder.query<OwnerOperator, string>({
      query: (id) => ({
        url: `/owner-operators/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: { data: OwnerOperator }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'OwnerOperators' as const, id }]
    }),
    updateOwnerOperator: builder.mutation<OwnerOperator, UpdateOwnerOperatorParams>({
      query: (data) => {
        const { id, ...restData } = data;
        return {
          url: `/owner-operators/${id}`,
          method: 'PUT',
          body: {
            ...restData
          }
        };
      },
      invalidatesTags: (result, error, data) => [
        { type: 'OwnerOperators', id: data.id },
        { type: 'OwnerOperators', id: 'OWNER_OPERATORS_LIST' }
      ]
    }),
    deleteOwnerOperator: builder.mutation<void, string>({
      query: (id) => ({
        url: `/owner-operators/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'OwnerOperators', id: 'OWNER_OPERATORS_LIST' }]
    }),
    // Owner Operator Trip Pay Defaults
    createOOTripPayDefault: builder.mutation<
      any,
      { ownerOperatorId: string; data: Partial<SettlementDetailDriverPay> }
    >({
      query: (data) => {
        const { ownerOperatorId } = data;
        return {
          url: `/owner-operators/${ownerOperatorId}/trip-driver-pay-default`,
          method: 'POST',
          body: data.data
        };
      }
    }),
    getOOTripPayDefault: builder.query<any, string>({
      query: (id) => ({
        url: `/owner-operators/${id}/trip-driver-pay-default`,
        method: 'GET'
      }),
      transformResponse: (response: { data: OwnerOperatorTripPayDefault[] }) => response.data
    }),
    updateOOTripPayDefault: builder.mutation<
      any,
      { ownerOperatorId: string; data: Partial<SettlementDetailDriverPay> }
    >({
      query: (data) => {
        const { ownerOperatorId } = data;
        return {
          url: `/owner-operators/${ownerOperatorId}/trip-driver-pay-default`,
          method: 'PUT',
          body: data.data
        };
      },
      transformResponse: (response: { data: any }) => response.data
    }),
    deleteOOTripPayDefault: builder.mutation<any, { ownerOperatorId: string; id: string }>({
      query: (data) => {
        const { ownerOperatorId } = data;
        return {
          url: `/owner-operators/${ownerOperatorId}/trip-driver-pay-default`,
          method: 'DELETE',
          body: { id: data.id }
        };
      }
    }),
    // Owner Operator Settlement(Period) Pay Defaults
    createOOPeriodPayDefault: builder.mutation<
      any,
      { ownerOperatorId: string; data: Partial<SettlementDetailDriverPay> }
    >({
      query: (data) => {
        const { ownerOperatorId } = data;
        return {
          url: `/owner-operators/${ownerOperatorId}/settlement-adjustments-default`,
          method: 'POST',
          body: data.data
        };
      }
    }),
    getOOPeriodPayDefault: builder.query<any, string>({
      query: (id) => ({
        url: `/owner-operators/${id}/settlement-adjustments-default`,
        method: 'GET'
      }),
      transformResponse: (response: { data: any }) => response.data
    }),
    updateOOPeriodPayDefault: builder.mutation<
      any,
      { ownerOperatorId: string; data: Partial<SettlementDetailDriverPay> }
    >({
      query: (data) => {
        const { ownerOperatorId } = data;
        return {
          url: `/owner-operators/${ownerOperatorId}/settlement-adjustments-default`,
          method: 'PUT',
          body: data.data
        };
      }
    }),
    deleteOOPeriodPayDefault: builder.mutation<any, { ownerOperatorId: string; id: string }>({
      query: (data) => {
        const { ownerOperatorId } = data;
        return {
          url: `/owner-operators/${ownerOperatorId}/settlement-adjustments-default`,
          method: 'DELETE',
          body: { id: data.id }
        };
      }
    }),
    getOwnerOperatorDrivers: builder.query<any, string>({
      query: (id) => ({
        url: `/owner-operators/${id}/drivers`,
        method: 'GET'
      })
    })
  })
});

export const {
  useCreateOwnerOperatorMutation,
  useGetOwnerOperatorsQuery,
  useGetOwnerOperatorByIdQuery,
  useUpdateOwnerOperatorMutation,
  useDeleteOwnerOperatorMutation,
  useCreateOOTripPayDefaultMutation,
  useGetOOTripPayDefaultQuery,
  useUpdateOOTripPayDefaultMutation,
  useDeleteOOTripPayDefaultMutation,
  useCreateOOPeriodPayDefaultMutation,
  useGetOOPeriodPayDefaultQuery,
  useUpdateOOPeriodPayDefaultMutation,
  useDeleteOOPeriodPayDefaultMutation,
  useGetOwnerOperatorDriversQuery
} = ownerOperatorsApi;
